import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import './PlayerView.css';
import { getTournament, savePlayer, getPlayer } from './api.js';
import ls from 'local-storage';
import Court from "./Court";
import logo from './img/2025/BT-logga-med-vit-kant.webp';
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { TOURNAMENT_TYPES } from './App.js';

const PlayerView = () => {

    const { tournamentId } = useParams();
    const [tournament, setTournament] = useState(null);
    const [currentRoundIndex, setCurrentRoundIndex] = useState(null);
    const [nextRoundIndex, setNextRoundIndex] = useState(null);
    const [player, setPlayer] = useState(ls.get("player") || "");
    const [allPlayerRounds, setAllPlayerRounds] = useState(null);
    const [playerName, setPlayerName] = useState(null);
    const [active, setActive] = useState(true);
    const [version, setVersion] = useState(1);
    const [error, setError] = useState(null);
    const [updateServer, setUpdateServer] = useState(false);
    const [debounceTimer, setDebounceTimer] = useState(null)

    const tournamentRef = useRef(tournament);
    const playerRef = useRef(player);

    const debounce = (func, delay) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), delay);
        };
    };

    //Load tournament & player + set up focus/visility listeners on load
    useEffect(() => {
        updateTournament();

        const handleVisibilityChange = debounce(() => {
            if (document.visibilityState === 'visible') {
                updateTournament();
            }
        }, 200);

        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('resume', handleVisibilityChange);
        window.addEventListener('focus', handleVisibilityChange);
        window.addEventListener('pageshow', handleVisibilityChange);
        window.addEventListener('online', handleVisibilityChange);
        window.addEventListener('popstate', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('resume', handleVisibilityChange);
            window.removeEventListener('focus', handleVisibilityChange);
            window.removeEventListener('pageshow', handleVisibilityChange);
            window.removeEventListener('online', handleVisibilityChange);
            window.removeEventListener('popstate', handleVisibilityChange);
        };
        // eslint-disable-next-line
    }, []);

    const updateTournament = async () => {
        const tournament = await getTournament(tournamentId);
        if (tournament === null || tournament.rounds.length === 0) {
            setError("Nothing to be found here yet. Are you early? Did you use the correct link/QR code?");
            return;
        } else {
            setError(null);
        }
        setTournament(tournament);
        tournamentRef.current = tournament;
        if (tournament.isLatestRoundStarted) {
            setCurrentRoundIndex(tournament.presentationRoundIndex > -1 ? tournament.presentationRoundIndex : null);
            setNextRoundIndex(null);
        } else {
            setCurrentRoundIndex(tournament.presentationRoundIndex > 0 ? tournament.presentationRoundIndex - 1 : null);
            setNextRoundIndex(tournament.presentationRoundIndex > -1 ? tournament.presentationRoundIndex : null);
        }
        updatePlayerData();
    }

    const updatePlayerData = async () => {
        if (playerRef.current !== "" && tournamentRef.current && tournamentRef.current.rounds) {
            const playerRounds = {};
            const cloudPlayer = await getPlayer(tournamentId, playerRef.current)
            const rounds = tournamentRef.current.rounds;
            for (let r = 0; r < rounds.length; r++) {
                let foundInRound = false;
                for (let c = 0; c < rounds[r].length && !foundInRound; c++) {
                    for (let t = 0; t < rounds[r][c].length && !foundInRound; t++) {
                        for (let p = 0; p < rounds[r][c][t].length && !foundInRound; p++) {
                            if (rounds[r][c][t][p] === playerRef.current) {
                                playerRounds[r] = {
                                    'courtIndex': c,
                                    'courtPlayers': rounds[r][c],
                                    'result': cloudPlayer && cloudPlayer[r] ? cloudPlayer[r].result : null
                                }
                                foundInRound = true;
                            }
                        }
                    }
                }
            }
            setAllPlayerRounds(playerRounds);
            setPlayerName((cloudPlayer && cloudPlayer.displayName) ? cloudPlayer.displayName : "");
            setActive(cloudPlayer ? cloudPlayer.active : true);
            setVersion(cloudPlayer ? cloudPlayer.version : 1);
        }
        return version
    };

    useEffect(() => {
        if (updateServer) {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            const timer = setTimeout(async () => {
                const playerData = structuredClone(allPlayerRounds);
                playerData.version = version;
                const response = await savePlayer(tournamentId, player, playerData);
                await updatePlayerData();
                if (response.status === 200) {
                    toast.success("Result saved!");
                } else {
                    toast.error("Failed to save result. Please try again.");
                }
            }, 1000);
            setDebounceTimer(timer);
        }
        setUpdateServer(false);
        // eslint-disable-next-line
    }, [updateServer]);

    const handlePlayerChange = (e) => {
        const value = e.target.value;
        if (value === "") {
            setPlayer("");
            playerRef.current = "";
            ls.set("player", "");
        } else {
            const number = parseInt(value, 10);
            setPlayer(number);
            playerRef.current = number;
            ls.set("player", number);
            console.log("Selected player: " + number);
        }
        updatePlayerData();
    };

    const toggleResult = (roundIndex) => {

        const allResults = ["W", "L", null];
        const nextResult = allResults[(allResults.indexOf(allPlayerRounds[roundIndex].result) + 1) % allResults.length];
        console.log("Next result: ", nextResult);
        console.log("All rounds: ", allPlayerRounds);
        setAllPlayerRounds((prevValue) => ({
            ...prevValue,
            [roundIndex]: {
                ...prevValue[roundIndex],
                result: nextResult
            }
        }));
        updateSwissResults(roundIndex, nextResult);
        setUpdateServer(true);
    }

    const updateSwissResults = async (roundIndex, newResult) => {
        if (tournament.tournamentType !== TOURNAMENT_TYPES.SWISS) {
            return;
        }
        let teamMates, playersOnCourt;
        allPlayerRounds[roundIndex].courtPlayers.forEach((team, _) => {
            if (team.includes(player)) {
                teamMates = team.filter(p => p !== player);
            }
        });
        playersOnCourt = allPlayerRounds[roundIndex].courtPlayers.flat().filter(p => p !== player);

        const opponentsResult = newResult === "W" ? "L" : (newResult === "L" ? "W" : null);

        for (const courtPlayer of playersOnCourt) {
            await updateOtherPlayerResults(courtPlayer, roundIndex, teamMates.includes(courtPlayer) ? newResult : opponentsResult);
        }
    }

    const updateOtherPlayerResults = async (playerId, roundIndex, result) => {
        getPlayer(tournamentId, playerId).then(player => {
            const newPlayer = player ? {
                ...player,
                [roundIndex]: { ...player[roundIndex], result: result }
            } : { [roundIndex]: { result: result } };
            savePlayer(tournamentId, playerId, newPlayer);
        });
    }

    const noCourtMessage = player === "" ? "Enter your player number above." : (allPlayerRounds && !allPlayerRounds[currentRoundIndex] && !allPlayerRounds[nextRoundIndex] ? "You are not playing this round." : null);

    function getCourtName(courtIndex) {
        const court = parseInt(courtIndex, 10);
        if (tournament.courtsToUse && tournament.courtsToUse[court]) {
            return tournament.courtsToUse[court];
        }
        return court + 1;
    }

    const deactivatePlayer = async () => {
        const confirmDeactivation = window.confirm(`Are you sure you want to withdraw player ${player} ${playerName}? This removes you from future rounds, but you can rejoin later. Please play any round already created/started as you will only be removed from future rounds.`);
        if (!confirmDeactivation) return;

        setActive(false);

        try {
            await savePlayer(tournamentId, player, { active: false, version: version });
            await updatePlayerData();
        } catch (error) {
            console.error("Failed to deactivate player:", error);
        }
    };

    const activatePlayer = async () => {
        const confirmDeactivation = window.confirm(`Are you sure you want player ${player} ${playerName} to rejoin?`);
        if (!confirmDeactivation) return;

        setActive(true);

        try {
            await savePlayer(tournamentId, player, { active: true, version: version });
            await updatePlayerData();
        } catch (error) {
            console.error("Failed to activate player:", error);
        }
    };

    const playerInstructions = (tournament && tournament.playerInstructions)
        ? <Link to={tournament.playerInstructions} target="_blank" className="player-instructions-link">Player Instructions</Link>
        : null;

    return (
        <div id="playerView">
            <ToastContainer />
            <img src={logo} alt="Tournament Logo" className="logo" />
            {error && <p>{error}</p>}
            {!error &&
                <>
                    <div id="playerInputRow">
                        <input
                            id="playerInput"
                            type="number"
                            min="0"
                            max="1000"
                            value={player}
                            onChange={handlePlayerChange}
                            placeholder="Your Number"
                        />
                    </div>
                    <p id="playerIdRow">
                        <span className={`player color${player % 10}`}>{player}</span>
                        <span className="playerName">{playerName}</span>
                    </p>
                    {allPlayerRounds && allPlayerRounds[nextRoundIndex] &&
                        <>
                            <p className="roundInfo">Your next round is on <span
                                className="courtInfo">Court {getCourtName(allPlayerRounds[nextRoundIndex].courtIndex)}</span>
                            </p>
                            <Court teams={allPlayerRounds[nextRoundIndex].courtPlayers}
                                key={allPlayerRounds[nextRoundIndex].courtIndex}
                                courtNumber={allPlayerRounds[nextRoundIndex].courtIndex + 1}
                                courtClass="courtSize3" />
                        </>
                    }
                    {allPlayerRounds && allPlayerRounds[currentRoundIndex] && !allPlayerRounds[nextRoundIndex] &&
                        <>
                            <p className="roundInfo">Your current round is on <span
                                className="courtInfo">Court {getCourtName(allPlayerRounds[currentRoundIndex].courtIndex)}</span>
                            </p>
                            <Court teams={allPlayerRounds[currentRoundIndex].courtPlayers}
                                key={allPlayerRounds[currentRoundIndex].courtIndex}
                                courtNumber={allPlayerRounds[currentRoundIndex].courtIndex + 1}
                                courtClass="courtSize3" />
                        </>
                    }
                    {noCourtMessage !== null &&
                        <p>
                            {noCourtMessage}
                        </p>
                    }

                    {allPlayerRounds && (
                        <>
                            <h2 id="playerResults">Player results</h2>
                            <p id="explanation">Click the <span className="circle neutral">?</span> next to the round until
                                you get <span className="circle win">W</span> for Win
                                or <span className="circle lose">L</span> if you lost.</p>
                            <ul id="allRounds">
                                {Object.entries(allPlayerRounds)
                                    .sort(([roundIndexA], [roundIndexB]) => parseInt(roundIndexB) - parseInt(roundIndexA))
                                    .map(([roundIndex, { courtIndex, courtPlayers, result }]) => (
                                        <li key={roundIndex}>
                                            <div onClick={() => toggleResult(roundIndex)}
                                                className={`circle ${result === null ? "neutral" : (result === "W" ? "win" : "lose")}`}>{result === null ? "W/L?" : (result === "W" ? "W" : "L")}</div>
                                            <strong>Round {parseInt(roundIndex, 10) + 1}:&nbsp;</strong>
                                            {tournament.paradiseMode ? courtPlayers.map(team => team.join(", ")).join(", ") : courtPlayers.map(team => team.join(" & ")).join(" vs ")}
                                            &nbsp;on
                                            Court {getCourtName(courtIndex)} {currentRoundIndex === parseInt(roundIndex) ? " (current)" : (nextRoundIndex === parseInt(roundIndex) ? " (next)" : "")}
                                        </li>
                                    ))}
                            </ul>
                        </>
                    )}
                    <Link to={`/leaderboard/${tournamentId}`} target="_blank" className="leaderboard-link">
                        Leaderboard
                    </Link>
                    {playerInstructions}
                    {player && active && (
                        <button onClick={deactivatePlayer} className="deactivate-button">
                            I want to take a break
                        </button>
                    )}
                    {player && !active && (
                        <button onClick={activatePlayer} className="activate-button">
                            I want to rejoin
                        </button>
                    )}
                </>}
        </div>
    );
};

export default PlayerView;
